import fs from "fs/promises";
import type { GetStaticProps, InferGetStaticPropsType, NextPage } from "next";
import Head from "next/head";
import path from "path";

import ProductPage from "@/components/Product/ProductPage";
import config from "@/config";
import {
  DEFAULT_LOCALE,
  LOCALES,
  SITE_ORIGIN,
} from "@/features/i18n/i18n.config.mjs";
import { fetchServerTranslations } from "@/i18n.server";
import { getAllProductPaths } from "@/lib/api/service/products/helpers";
import { featureLogger } from "@/lib/logger";
import fetchSiteSettings from "@/lib/storyblok/fetchSiteSettings";

import type { ProductPageData } from "../api/service/products/[...langAndUri]";
import { getFullProductPage } from "../api/service/products/[...langAndUri]";

const log = featureLogger("product");

// type NextPath = {
//   params: {
//     product: string;
//   };
//   locale?: string;
// };

// const giftWrappingSlug = process.env.GIFT_WRAPPING_SLUG;

type PathMap = Record<
  string,
  Awaited<ReturnType<typeof getAllProductPaths>>[number]
>;

const FILENAME = "locale-paths.json";
const FOLDER = "data";

function getAlternateUrls(translatedUris: Record<string, string>) {
  return Object.entries(translatedUris).reduce(
    (acc, [locale, uri]) => {
      if (!uri || !LOCALES.includes(locale)) return acc;
      const prefix = locale === DEFAULT_LOCALE ? "" : `/${locale}`;
      return {
        ...acc,
        [locale]: `${SITE_ORIGIN}${prefix}/product/${uri}`,
      };
    },
    {} as Record<string, string>
  );
}

const Product: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  product,
  alternates,
  canonical,
  xDefault,
}) => {
  return (
    <>
      <Head>
        {canonical && <link rel="canonical" href={canonical} />}
        {alternates &&
          Object.entries(alternates).map(([key, value]) => (
            <link key={key} rel="alternate" href={value} hrefLang={key} />
          ))}
        {xDefault && (
          <link rel="alternate" href={xDefault} hrefLang="x-default" />
        )}
      </Head>
      <ProductPage product={product} />
    </>
  );
};

export default Product;

export const getStaticProps: GetStaticProps<{
  product: ProductPageData;
  alternates: Record<string, string> | null;
  canonical: string | null;
  xDefault: string | null;
}> = async (context) => {
  const locale = context?.locale ?? DEFAULT_LOCALE;
  try {
    const uri = context?.params?.product as string;

    let notFound = false;

    const [product, siteSettings] = await Promise.all([
      getFullProductPage(uri, locale),
      fetchSiteSettings(locale),
    ]);

    let alternates: Record<string, string> | null = null;
    try {
      const variant =
        product.data.variants?.variants[product.data.variants?.default];
      const pathMap = JSON.parse(
        await fs.readFile(path.join(process.cwd(), FOLDER, FILENAME), "utf-8")
      ) as PathMap;

      const translation = variant?.info?.details?.id
        ? pathMap[variant?.info?.details?.id]
        : null;

      const translations = translation?.lang ?? null;

      alternates = translations ? getAlternateUrls(translations) : null;
    } catch (error) {
      log.warn("product_alternates_unavailable", {
        uri,
        locale,
        fallback: "canonical_only",
        error,
      });
    }

    const canonical =
      alternates?.[locale] ??
      `${SITE_ORIGIN}${
        locale === DEFAULT_LOCALE ? "" : `/${locale}`
      }/product/${uri}`;
    const xDefault = alternates?.[DEFAULT_LOCALE] ?? null;

    let localePaths: Record<string, string> | null = null;
    try {
      const variant =
        product.data.variants?.variants[product.data.variants?.default];
      const pathMap = JSON.parse(
        await fs.readFile(path.join(process.cwd(), FOLDER, FILENAME), "utf-8")
      ) as PathMap;
      const translation = variant?.info?.details?.id
        ? pathMap[variant?.info?.details?.id]
        : null;
      const translations = translation?.lang;
      if (translations) {
        localePaths = Object.entries(translations).reduce(
          (acc, [lng, slug]) => {
            if (slug && LOCALES.includes(lng)) {
              acc[lng] = `/product/${slug}`;
            }
            return acc;
          },
          {} as Record<string, string>
        );
      }
    } catch {
      // localePaths optional; falls back to router.asPath in the switcher
    }

    if (!product) {
      notFound = true;
    }

    return {
      props: {
        ...(await fetchServerTranslations(locale)),
        product,
        alternates,
        canonical,
        xDefault,
        localePaths,
        siteSettings,
      },
      notFound,
      revalidate: config.revalidate,
    };
  } catch {
    return {
      props: {
        ...(await fetchServerTranslations(locale)),
        product: null,
        alternates: null,
        canonical: null,
        xDefault: null,
        localePaths: null,
        siteSettings: null,
      },
      notFound: true,
      revalidate: config.revalidate,
    };
  }
};

export const getStaticPaths = async () => {
  const rawPaths = await getAllProductPaths();

  const pathMap = rawPaths.reduce((acc, curr) => {
    if (!(curr.id in acc)) {
      acc[curr.id] = curr;
    }
    return acc;
  }, {} as PathMap);

  await fs.mkdir(path.join(process.cwd(), FOLDER), { recursive: true });
  await fs.writeFile(
    path.join(process.cwd(), FOLDER, FILENAME),
    JSON.stringify(pathMap, null, 2),
    "utf-8"
  );

  const paths = rawPaths.flatMap((p) => {
    // lang: { en: 'lynild-large-throw', no: 'lynild-store-pledd', de: ... }
    // Centra returns more locales than the site serves; filter to LOCALES.
    return Object.keys(p.lang)
      .filter((locale) => LOCALES.includes(locale))
      .map((locale) => ({
        params: {
          product: p.lang[locale],
        },
        locale,
      }));
  });

  return {
    paths,
    fallback: "blocking",
  };
};
